import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  Image,
} from "@chakra-ui/react";
import { FiDownload, FiPrinter, FiRotateCcw } from "react-icons/fi";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { getErrorMessage } from "../../functions";
import { downloadPdf } from "../../services/invoiceApi";
import BasicModal from "../../UI/BasicModal";
import ConfirmDialog from "../../UI/ConfirmDialog";
import PageHeader from "../../UI/PageHeader";
import { buildCatalogHtml } from "./catalogHtml";
import {
  addColumn,
  addRow,
  addSchemeLine,
  createDefaultCatalog,
  loadCatalog,
  readLogoFile,
  removeColumn,
  removeRow,
  removeSchemeLine,
  saveCatalog,
  setLogoDataUrl,
  updateCell,
  updateColumnLabel,
  updateMeta,
  updateSchemeLine,
} from "./catalogState";

export default function ProductCatalog() {
  const companyDetails = useSelector(
    (state) => state.companyDetails.companyDetails
  );
  const [catalog, setCatalog] = useState(() => loadCatalog());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const fileRef = useRef(null);

  const catalogPreviewHtml = useMemo(
    () => buildCatalogHtml(catalog, companyDetails),
    [catalog, companyDetails]
  );

  useEffect(() => {
    saveCatalog(catalog);
  }, [catalog]);

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const res = await readLogoFile(file);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    setCatalog((c) => setLogoDataUrl(c, res.dataUrl));
    toast.success("Logo updated");
  };

  const clearLogo = () => {
    setCatalog((c) => setLogoDataUrl(c, null));
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfLoading(true);
      const response = await downloadPdf({
        html: buildCatalogHtml(catalog, companyDetails),
      });
      if (!response.ok) {
        throw new Error("PDF request failed");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${
          (
            companyDetails?.businessName ||
            catalog.meta.businessName ||
            "catalog"
          ).replace(/\s+/g, "-") || "catalog"
        }.pdf`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setPdfLoading(false);
    }
  };

  const printCatalog = () => {
    const html = buildCatalogHtml(catalog, companyDetails);
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Catalog print");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none";
    document.body.appendChild(iframe);

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      iframe.remove();
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(cleanup, 800);
      }
    };
    iframe.onerror = () => {
      cleanup();
      toast.error("Could not prepare print preview");
    };
    iframe.src = objectUrl;
  };

  const confirmReset = () => {
    setCatalog(createDefaultCatalog());
    setShowResetConfirm(false);
    toast.success("Restored default catalog");
  };

  return (
    <div>
      <PageHeader
        title="Product catalog"
        description="Build a dealer price list with your columns and rows. Download a PDF for customer pitches—same flow as invoices."
        actions={
          <>
            <Button variant="outline" onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
            <Button
              variant="outline"
              leftIcon={<FiPrinter />}
              onClick={printCatalog}
            >
              Print
            </Button>
            <Button
              colorScheme="brand"
              leftIcon={<FiDownload />}
              onClick={handleDownloadPdf}
              isLoading={pdfLoading}
            >
              Download PDF
            </Button>
            <Button
              variant="outline"
              leftIcon={<FiRotateCcw />}
              onClick={() => setShowResetConfirm(true)}
            >
              Reset to default
            </Button>
          </>
        }
      />

      <Card borderRadius="xl" shadow="sm" borderWidth="1px" mb={6}>
        <CardBody>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={3}>
            Branding
          </Text>
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={6}
            align={{ base: "stretch", md: "flex-start" }}
          >
            <Box flexShrink={0}>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                hidden
                onChange={handleLogoChange}
              />
              <Box
                w="140px"
                h="72px"
                borderWidth="2px"
                borderStyle="dashed"
                borderColor="gray.300"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="gray.50"
                overflow="hidden"
              >
                {catalog.logoDataUrl ? (
                  <Image
                    src={catalog.logoDataUrl}
                    alt="Logo"
                    maxH="68px"
                    maxW="136px"
                    objectFit="contain"
                  />
                ) : (
                  <Text
                    fontSize="10px"
                    fontWeight="bold"
                    color="gray.400"
                    textAlign="center"
                    px={2}
                  >
                    Your logo
                  </Text>
                )}
              </Box>
              <Flex gap={2} mt={2}>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                >
                  Replace logo
                </Button>
                {catalog.logoDataUrl ? (
                  <Button size="xs" variant="ghost" onClick={clearLogo}>
                    Remove
                  </Button>
                ) : null}
              </Flex>
            </Box>
            <VStack align="stretch" flex={1} spacing={3}>
              <Box
                borderWidth="1px"
                borderRadius="lg"
                borderColor="gray.200"
                bg="gray.50"
                px={3}
                py={2.5}
              >
                <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                  Business &amp; contact on PDF
                </Text>
                <Text fontSize="xs" color="gray.500" mb={2} lineHeight="1.5">
                  Pulled from <strong>Company profile</strong> (sidebar →
                  Account). Update there to change name, phone, email, address,
                  and GST on the catalog.
                </Text>
                <VStack align="stretch" spacing={1} fontSize="xs" color="gray.700">
                  <Text>
                    <Text as="span" color="gray.500">
                      Name:{" "}
                    </Text>
                    {companyDetails?.businessName || "—"}
                  </Text>
                  <Text>
                    <Text as="span" color="gray.500">
                      Phone:{" "}
                    </Text>
                    {companyDetails?.phoneNumber || "—"}
                  </Text>
                  <Text>
                    <Text as="span" color="gray.500">
                      Email:{" "}
                    </Text>
                    {companyDetails?.email || "—"}
                  </Text>
                  <Text noOfLines={3}>
                    <Text as="span" color="gray.500">
                      Address:{" "}
                    </Text>
                    {companyDetails?.address || "—"}
                  </Text>
                  <Text>
                    <Text as="span" color="gray.500">
                      GST:{" "}
                    </Text>
                    {companyDetails?.gst || "—"}
                  </Text>
                </VStack>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                  Website
                </Text>
                <Input
                  size="sm"
                  placeholder="https://…"
                  value={catalog.meta.website}
                  onChange={(e) =>
                    setCatalog((c) => updateMeta(c, { website: e.target.value }))
                  }
                  borderRadius="lg"
                />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                  Tagline
                </Text>
                <Input
                  size="sm"
                  placeholder="Short strapline under your business name on the PDF"
                  value={catalog.meta.tagline ?? ""}
                  onChange={(e) =>
                    setCatalog((c) =>
                      updateMeta(c, { tagline: e.target.value })
                    )
                  }
                  borderRadius="lg"
                />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                  Company intro
                </Text>
                <Textarea
                  size="sm"
                  placeholder="Optional — a short paragraph for the PDF cover area."
                  value={catalog.meta.intro ?? ""}
                  onChange={(e) =>
                    setCatalog((c) => updateMeta(c, { intro: e.target.value }))
                  }
                  borderRadius="lg"
                  rows={3}
                />
              </Box>
            </VStack>
          </Flex>
        </CardBody>
      </Card>

      <Flex
        wrap="wrap"
        gap={2}
        mb={4}
        justify={{ base: "stretch", md: "flex-start" }}
      >
        <Button
          size="sm"
          leftIcon={<FaPlus />}
          variant="outline"
          onClick={() => setCatalog((c) => addColumn(c))}
        >
          Add column
        </Button>
        <Button
          size="sm"
          leftIcon={<FaPlus />}
          colorScheme="brand"
          onClick={() => setCatalog((c) => addRow(c))}
        >
          Add row
        </Button>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<FiRotateCcw />}
          onClick={() => setShowResetConfirm(true)}
        >
          Reset to default
        </Button>
      </Flex>

      <Box overflowX="auto" borderWidth="1px" borderRadius="xl" mb={8}>
        <Table variant="premium" size="sm" minW="full">
          <Thead>
            <Tr>
              {catalog.columns.map((col) => (
                <Th key={col.id} whiteSpace="nowrap" maxW="220px">
                  <Flex align="center" gap={1}>
                    <Input
                      size="sm"
                      value={col.label}
                      fontWeight="semibold"
                      onChange={(e) =>
                        setCatalog((c) =>
                          updateColumnLabel(c, col.id, e.target.value)
                        )
                      }
                      borderRadius="md"
                      flex={1}
                      minW="72px"
                    />
                    <IconButton
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      aria-label={`Remove column ${col.label}`}
                      icon={<MdClose size={16} />}
                      isDisabled={catalog.columns.length <= 1}
                      onClick={() =>
                        setCatalog((c) => removeColumn(c, col.id))
                      }
                    />
                  </Flex>
                </Th>
              ))}
              <Th w="56px"> </Th>
            </Tr>
          </Thead>
          <Tbody>
            {catalog.rows.map((row) => (
              <Tr key={row.id}>
                {catalog.columns.map((col) => (
                  <Td key={col.id} verticalAlign="top">
                    <Input
                      size="sm"
                      value={row.cells[col.id] ?? ""}
                      onChange={(e) =>
                        setCatalog((c) =>
                          updateCell(c, row.id, col.id, e.target.value)
                        )
                      }
                      borderRadius="md"
                    />
                  </Td>
                ))}
                <Td>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Delete row"
                    icon={<FaTrashAlt size={14} />}
                    onClick={() => setRowToDelete(row.id)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Card borderRadius="xl" shadow="sm" borderWidth="1px">
        <CardBody>
          <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={2}>
            <Heading size="sm" fontWeight="semibold" color="gray.800">
              Scheme
            </Heading>
            <Button
              size="sm"
              leftIcon={<FaPlus />}
              variant="outline"
              onClick={() => setCatalog((c) => addSchemeLine(c))}
            >
              Add line
            </Button>
          </Flex>
          <VStack align="stretch" spacing={3}>
            {catalog.schemes.map((s) => (
              <Flex key={s.id} gap={2} align="flex-start">
                <Textarea
                  size="sm"
                  value={s.body}
                  onChange={(e) =>
                    setCatalog((c) =>
                      updateSchemeLine(c, s.id, e.target.value)
                    )
                  }
                  borderRadius="lg"
                  rows={2}
                  flex={1}
                />
                <IconButton
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  aria-label="Remove scheme line"
                  icon={<FaTrashAlt size={14} />}
                  onClick={() =>
                    setCatalog((c) => removeSchemeLine(c, s.id))
                  }
                />
              </Flex>
            ))}
          </VStack>
          <Box mt={4}>
            <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
              Scheme disclaimer (shown under the list in PDF)
            </Text>
            <Textarea
              size="sm"
              value={catalog.meta.schemeDisclaimer}
              onChange={(e) =>
                setCatalog((c) =>
                  updateMeta(c, { schemeDisclaimer: e.target.value })
                )
              }
              borderRadius="lg"
              rows={2}
            />
          </Box>
          <Box mt={4}>
            <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
              General disclaimer (customer-facing, above the confidential
              footer)
            </Text>
            <Textarea
              size="sm"
              placeholder="Shown at the bottom of the catalogue for customers"
              value={catalog.meta.footnoteDisclaimer ?? ""}
              onChange={(e) =>
                setCatalog((c) =>
                  updateMeta(c, { footnoteDisclaimer: e.target.value })
                )
              }
              borderRadius="lg"
              rows={2}
            />
          </Box>
          <Box mt={4}>
            <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
              Footer note (optional, below scheme on PDF)
            </Text>
            <Textarea
              size="sm"
              placeholder="Extra terms, contact, validity…"
              value={catalog.meta.footerNote}
              onChange={(e) =>
                setCatalog((c) =>
                  updateMeta(c, { footerNote: e.target.value })
                )
              }
              borderRadius="lg"
              rows={2}
            />
          </Box>
        </CardBody>
      </Card>

      <BasicModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Catalog preview"
        size="full"
      >
        <Text fontSize="sm" color="gray.600" mb={3}>
          Client-ready layout: identical to PDF. Scroll inside the page for the
          full catalogue.
        </Text>
        <Box
          borderRadius="lg"
          overflow="hidden"
          bg="gray.50"
          p={{ base: 2, md: 4 }}
          minH={{ base: "74vh", md: "calc(100vh - 11rem)" }}
          maxH="calc(100vh - 6rem)"
        >
          <iframe
            title="Catalog preview"
            srcDoc={catalogPreviewHtml}
            style={{
              width: "100%",
              height: "100%",
              minHeight: "min(84vh, 960px)",
              border: "none",
              borderRadius: "10px",
              background: "#fdfcfa",
              boxShadow:
                "0 0 0 1px rgba(20,18,15,0.06), 0 12px 40px rgba(20,18,15,0.08)",
            }}
          />
        </Box>
      </BasicModal>

      <ConfirmDialog
        isOpen={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title="Remove row?"
        body="This product row will be removed from your catalog."
        confirmText="Remove"
        onConfirm={() => {
          if (rowToDelete) {
            setCatalog((c) => removeRow(c, rowToDelete));
            setRowToDelete(null);
            toast.success("Row removed");
          }
        }}
        isDestructive
      />

      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="Reset to default catalog?"
        body="Extra columns will be removed, the original column set and labels will be restored, and sample rows, schemes, and catalog-only branding (tagline, intro, website in this editor) will return to their initial values. Your logo will be cleared. Company profile data is not changed. This cannot be undone."
        confirmText="Reset to default"
        onConfirm={confirmReset}
        isDestructive
      />
    </div>
  );
}
